import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

const API = import.meta.env.VITE_API_URL || '/api'

function getHeaders() {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  }
}

interface Counts {
  services: number
  portfolio: number
  testimonials: number
  team: number
  blog: number
  stats: number
  clients: number
  messages: number
}

export default function AdminDashboard() {
  const [counts, setCounts] = useState<Counts>({
    services: 0, portfolio: 0, testimonials: 0, team: 0,
    blog: 0, stats: 0, clients: 0, messages: 0,
  })
  const [user, setUser] = useState<any>(null)
  const [confirmReset, setConfirmReset] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [resetDone, setResetDone] = useState(false)

  useEffect(() => {
    fetch(`${API}/auth/me`, { headers: getHeaders() })
      .then((r) => r.json())
      .then((d) => setUser(d.user))

    Promise.all([
      fetch(`${API}/services`).then(r => r.json()),
      fetch(`${API}/portfolio`).then(r => r.json()),
      fetch(`${API}/testimonials`).then(r => r.json()),
      fetch(`${API}/team`).then(r => r.json()),
      fetch(`${API}/blog`).then(r => r.json()),
      fetch(`${API}/stats`).then(r => r.json()),
      fetch(`${API}/clients`).then(r => r.json()),
      fetch(`${API}/contact/messages`, { headers: getHeaders() }).then(r => r.json()).catch(() => []),
    ]).then(([services, portfolio, testimonials, team, blog, stats, clients, messages]) => {
      setCounts({
        services: Array.isArray(services) ? services.length : 0,
        portfolio: Array.isArray(portfolio) ? portfolio.length : 0,
        testimonials: Array.isArray(testimonials) ? testimonials.length : 0,
        team: Array.isArray(team) ? team.length : 0,
        blog: Array.isArray(blog) ? blog.length : 0,
        stats: Array.isArray(stats) ? stats.length : 0,
        clients: Array.isArray(clients) ? clients.length : 0,
        messages: Array.isArray(messages) ? messages.length : 0,
      })
    })
  }, [])

  const cards = [
    { label: 'Services', count: counts.services, icon: '🎨', color: 'from-electric-blue to-purple-500' },
    { label: 'Portfolio', count: counts.portfolio, icon: '🖼️', color: 'from-emerald-400 to-teal-500' },
    { label: 'Testimonials', count: counts.testimonials, icon: '💬', color: 'from-amber-400 to-orange-500' },
    { label: 'Team', count: counts.team, icon: '👥', color: 'from-pink-400 to-rose-500' },
    { label: 'Blog Posts', count: counts.blog, icon: '📝', color: 'from-sky-400 to-blue-500' },
    { label: 'Stats', count: counts.stats, icon: '📈', color: 'from-fuchsia-400 to-pink-500' },
    { label: 'Clients', count: counts.clients, icon: '🏢', color: 'from-cyan-400 to-blue-500' },
    { label: 'Messages', count: counts.messages, icon: '✉️', color: 'from-violet-400 to-purple-500' },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        {user && <p className="text-gray-400 mt-1">Welcome back, {user.name}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-gray-900 border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-3xl">{card.icon}</span>
              <span className={`text-3xl font-bold bg-gradient-to-r ${card.color} bg-clip-text text-transparent`}>
                {card.count}
              </span>
            </div>
            <p className="text-gray-400 text-sm">{card.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 bg-gray-900 border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <a href="/admin/services" className="block p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
            <span className="text-xl mr-2">➕</span>
            <span className="text-sm text-gray-300">Add New Service</span>
          </a>
          <a href="/admin/portfolio" className="block p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
            <span className="text-xl mr-2">➕</span>
            <span className="text-sm text-gray-300">Add Portfolio Item</span>
          </a>
          <a href="/admin/blog" className="block p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
            <span className="text-xl mr-2">➕</span>
            <span className="text-sm text-gray-300">Write Blog Post</span>
          </a>
        </div>
      </div>

      <div className="mt-10 bg-gray-900 border border-red-500/20 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Reset to Defaults</h2>
            <p className="text-sm text-gray-400 mt-1">Restore all content to the original seed data. This cannot be undone.</p>
          </div>
          {resetDone ? (
            <div className="px-4 py-2 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg text-sm">Reset Complete!</div>
          ) : confirmReset ? (
            <div className="flex items-center gap-3">
              <button onClick={async () => {
                setResetting(true)
                try {
                  const r = await fetch(`${API}/reset`, { method: 'POST', headers: getHeaders() })
                  if (r.ok) { setResetDone(true); setConfirmReset(false); setTimeout(() => setResetDone(false), 4000) }
                } catch {}
                setResetting(false)
              }} disabled={resetting} className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-lg transition-colors">
                {resetting ? 'Resetting...' : 'Yes, Reset Everything'}
              </button>
              <button onClick={() => setConfirmReset(false)} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setConfirmReset(true)} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium rounded-lg transition-colors">
              Reset All Data
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
